import { Formik, Form } from 'formik'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation, Trans } from 'react-i18next'
import styled from 'styled-components'
import * as Yup from 'yup'
import {
  path,
  pick,
  applySpec,
  prop,
  map,
  pipe,
  propOr,
  flatten,
  values as rValues,
  length
} from 'ramda'
import { createModal, useModalize } from '@/components/modalize/mui'
import { toast } from '@/components/toast-notifications'
import { Button } from '@/components/button'
import { DeleteRed } from '@/components/icons'
import { colors } from '@/styles'
import { FieldInput } from '@/components/form'
import formatName from '@/utils/format-name'
import DeleteModalIcon from './delete-modal-icon.svg?react'
import DeleteModalInputIcon from './delete-modal-input-icon.svg?react'

const DeleteModalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px;
`

const DeleteModalListWrapper = styled.div`
  width: 100%;
  overflow-y: scroll;
  height: 205px;
  border: 1px solid ${colors.grey88};
  border-radius: 4px;
  margin-top: 24px;
`

const TitleWrapper = styled.h2`
  margin-top: 25px;
  font-size: 24px;
  line-height: 32px;
  font-weight: 800;
  text-align: center;
`

const TextWrapper = styled.p`
  margin-top: 25px;
  text-align: center;
  color: ${colors.grey48};
`

const ButtonWrapper = styled.div`
  margin-top: 25px;
  width: 100%;
  display: flex;
  justify-content: space-around;
`

const ButtonStyle = styled(Button)``

const ListItemWrapper = styled.div`
  display: flex;
  align-items: center;
  margin: 10px;
`

const ListItemLeft = styled.div`
  width: 40px;
`

const ListItemRight = styled.div`
  p {
    margin: 0;
  }
`

const ListItemRightName = styled(ListItemRight)``

const ListItemRightAddress = styled(ListItemRight)`
  font-size: 12px;
  color: ${colors.grey48};
`

const ConfirmationText = styled.h3`
  margin-bottom: 12px;
  font-size: 16px;
  line-height: 24px;
  font-weight: 800;
  text-align: center;
`

const ConfirmationTextInput = styled(FieldInput)``

const DeleteConfirmationModal = ({ selectedChat, history, setSelectMode, setIsCheckAll }) => {
  const { t } = useTranslation()
  const { close } = useModalize()
  const dispatch = useDispatch()

  const chat = useSelector(state => state.chat)
  const timelines = chat.timelines
  const selectedTimelines = pick(selectedChat, timelines)
  const selectedChatroomId = history?.location.pathname.split('/')[2]

  const formOptions = () => ({
    initialValues: {
      confirm_text: ''
    },
    validationSchema: Yup.object().shape({
      confirm_text: Yup.string()
        .matches(
          /^(DELETE)$/,
          t(
            'b.settings.edit.text_must_match.error',
            'Please enter the text exactly as displayed to confirm'
          )
        )
        .required()
    }),
    onSubmit: async values => {
      const { response, body } = await dispatch.chat.bulkTimelineDelete({
        body: selectedChat.map(item => Number(item))
      })

      if (response?.ok && length(body?.failed)) {
        const sucessChatRooms = length(body?.deleted)
        const failedChatRooms = pipe(rValues, flatten, length)(body?.failed)
        toast(
          t(
            'b.settings.edit.deleted_chat_rooms.text',
            `{{ success }} chat room(s) have been deleted! {{ failed }} chat room(s) are not deleted because they are in an active tenancy agreement.`,
            { success: sucessChatRooms, failed: failedChatRooms }
          ),
          {
            title: t('b.settings.edit.deleted_chat.title', 'Chat rooms deleted!'),
            type: 'success'
          }
        )
      }

      setSelectMode(false)
      setIsCheckAll(false)
      close()
      if (selectedChat.includes(selectedChatroomId)) history.replace(`/chat`)
    }
  })

  const unitAndBuilding = map(
    applySpec({
      unit: prop('units'),
      building: prop('buildings'),
      them: pipe(path(['user_ids', 'them']), propOr(null, 0))
    })
  )

  return (
    <Formik {...formOptions()}>
      {({ isSubmitting, isValid }) => (
        <Form className="flex-full">
          <DeleteModalWrapper>
            <DeleteModalIcon />
            <TitleWrapper>
              <Trans i18nKey="b.chat.view.bulk_edit.delete_modal.title">
                Are you sure you want to delete {`${selectedChat.length}`} chat conversations?
              </Trans>
            </TitleWrapper>
            <TextWrapper>
              {t(
                'b.chat.view.bulk_edit.delete_modal.sub_title',
                'The following chats will be permanently removed. This cannot be undone and you won’t be able to recover any messages or files.'
              )}
            </TextWrapper>
            <DeleteModalListWrapper>
              {Object.values(unitAndBuilding(selectedTimelines)).map(item => {
                return (
                  <ListItemWrapper>
                    <ListItemLeft>
                      <DeleteModalInputIcon />
                    </ListItemLeft>
                    <ListItemRight>
                      <ListItemRightName>{formatName(chat.users[item.them])}</ListItemRightName>
                      <ListItemRightAddress>
                        {!!item.unit.unit_number ? `${item.unit.unit_number} - ` : ''}{' '}
                        {item.building.full_street_name}
                      </ListItemRightAddress>
                    </ListItemRight>
                  </ListItemWrapper>
                )
              })}
            </DeleteModalListWrapper>
            <TextWrapper>
              {t(
                'b.chat.view.bulk_edit.delete_modal.note',
                'Please note: Chats in an active contract will not be deleted.'
              )}
            </TextWrapper>
            <ConfirmationText>Type DELETE below to confirm</ConfirmationText>
            <ConfirmationTextInput name="confirm_text" id="confirm_text" required />
            <ButtonWrapper>
              <ButtonStyle
                theme="delete"
                type="submit"
                disabled={!isValid}
                isSubmitting={isSubmitting}
              >
                <DeleteRed />
                {t('b.chat.view.bulk_edit.menu.delete_all', 'Delete all')}
              </ButtonStyle>
            </ButtonWrapper>
          </DeleteModalWrapper>
        </Form>
      )}
    </Formik>
  )
}

export const showDeleteConfirmationModal = createModal(DeleteConfirmationModal, {
  width: '440px',
  showClose: true
})
